<template>
  <div v-if="studyLoaded" class="view home">
    <div class="header">
      <MenuHelper />
    </div>

    <KeepAlive>
      <GMap v-if="!isDesktop || $route.name === 'result'" />
    </KeepAlive>

    <Filters v-if="!isDesktop || $route.name === 'result'" :isMap="true" />

    <MapLegend v-if="!isDesktop || $route.name === 'result'" />

    <div v-if="isDesktop" class="download-button" :title="$t('downloadMap')" @click="downloadMap">
      <i v-if="!isIE && !downloadLoading" class="icon-download" />
      <div v-if="downloadLoading" class="icon-loading" />
    </div>

    <AsideNav />

    <Transition name="translate">
      <DistributionRemote v-if="menu === 'TRANSIT' && !settingsOpen" />
    </Transition>

    <Transition name="translate">
      <FocusRemote v-if="menu === 'IMPACT' && !settingsOpen" />
    </Transition>

    <Transition name="translate">
      <EnvironmentalRemote v-if="menu === 'ENVIRONMENTAL' && !settingsOpen" />
    </Transition>

    <Transition name="translate">
      <SiteRemote v-if="targetSites.length > 4 && !settingsOpen && !isDesktop" />
    </Transition>

    <Transition name="translate">
      <SettingHelper v-if="settingsOpen" :sites="sites" :active="activeIndex" />
    </Transition>

    <RouterView v-slot="{ Component }">
      <Transition name="fade">
        <component :is="Component" />
      </Transition>
    </RouterView>

    <Tutorial v-if="!tutorialDone && isDesktop" />
  </div>
</template>

<script>
import AsideNav from '@/components/Map/AsideNav';
import DistributionRemote from '@/components/Map/DistributionRemote';
import EnvironmentalRemote from '@/components/Map/EnvironmentalRemote';
import Filters from '@/components/Map/Analysis/Filters';
import FocusRemote from '@/components/Map/FocusRemote';
import MenuHelper from '@/components/Map/MenuHelper';
import GMap from '@/components/Map';
import SettingHelper from '@/components/Map/SettingHelper';
import SiteRemote from '@/components/Map/SiteRemote';
import Tutorial from '@/components/Tutorial';
import MapLegend from '@/components/Map/MapLegend';

import { mapGetters } from 'vuex';

import html2canvas from 'html2canvas';

export default {
  components: {
    AsideNav,
    DistributionRemote,
    EnvironmentalRemote,
    Filters,
    FocusRemote,
    MenuHelper,
    GMap,
    SettingHelper,
    SiteRemote,
    Tutorial,
    MapLegend,
  },

  data() {
    return {
      tutorialDisplay: true,
      downloadLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      isDesktop: 'isDesktop',
      study: 'study',
      studyLoaded: 'studyLoaded',
      activeIndex: 'activeIndex',
      menu: 'menu',
      tutorialDone: 'tutorialDone',
    }),

    isIE() {
      const ua = navigator.userAgent;
      /* MSIE used to detect old browsers and Trident used to newer ones */
      return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    },

    sites() {
      return this.study && this.study.sites ? this.study.sites : [];
    },

    targetSites() {
      return this.sites.filter(site => site.type === 'Target');
    },

    settingsOpen() {
      return this.study && this.$route.hash === '' && (!this.isDesktop || this.$route.name === 'Controller');
    },
  },

  watch: {
    $route(newRoute, oldRoute) {
      if (newRoute.params.studyId !== oldRoute.params.studyId) {
        this.initStudy();
      }
    },
  },

  mounted() {
    this.initStudy();
  },

  beforeUnmount() {
    this.$store.dispatch('getStudy');
  },

  methods: {
    initStudy() {
      const id = this.$route.params.studyId;
      this.$store.dispatch('getStudyResult', { id });
    },

    downloadMap() {
      this.downloadLoading = true;
      html2canvas(document.querySelector('.gm-style'), {
        imageTimeout: 10000,
        useCORS: true,
      }).then(canvas => {
        let link = document.createElement('a');
        link.download = `map.png`;
        link.href = canvas.toDataURL();
        link.click();
        this.downloadLoading = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
@use '@/styles/animations.scss';

.home {
  display: flex;
  flex-direction: column;
}

.header {
  position: relative;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
}

.filters-container {
  position: fixed;
  width: auto;
  left: 54px;
  right: 0;
  top: 114px;
  pointer-events: none;
  z-index: 2;
}

.download-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  position: absolute;
  top: 114px;
  right: 138px;
  width: 37px;
  height: 37px;
  overflow: hidden;
  border-radius: 2px;
  font-size: 1.5em;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  transition: 0.4s ease;
  cursor: pointer;

  .icon-loading {
    width: 20px;
    height: 20px;
    border-radius: 20px;
    border: solid 4px colors.$navy-color;
    border-top-color: transparent;
    box-sizing: border-box;
    animation: rotating 2s linear infinite;
  }

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.35) 0 1px 5px -1px;
    color: colors.$white-color;
    background: colors.$navy-color;

    .icon-loading {
      border-color: colors.$white-color;
      border-top-color: transparent;
    }
  }
}
</style>
